import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useToast,
  VStack
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiMail, FiMoreVertical, FiSearch, FiShield, FiSlash, FiUserCheck, FiUserMinus, FiUsers } from "react-icons/fi";
import {
  approveGroupMember,
  blockGroupMember,
  demoteGroupMember,
  deleteGroupMember,
  getGroupMembers,
  GroupMemberDetail,
  GroupMembersMeta,
  GroupMemberStatus,
  promoteGroupMember,
  unblockGroupMember
} from "@lubbezposrednio-frontend/core";
import { mediaUrl } from "./wallShared";

// Osobne „worki": członkowie z kontem vs. zwykłe e-maile (bez konta) + oczekujący + zablokowani.
const STATUS_TABS: { key: GroupMemberStatus; label: string }[] = [
  { key: "account", label: "Konta" },
  { key: "email", label: "E-maile" },
  { key: "pending", label: "Oczekujący" },
  { key: "blocked", label: "Zablokowani" }
];

const BUCKET_HINT: Partial<Record<GroupMemberStatus, string>> = {
  email: "Zaproszeni e-mailem — nie mają konta, więc nie publikują ani nie komentują. Głosować mogą przez link."
};

const RoleBadge: React.FC<{ m: GroupMemberDetail }> = ({ m }) => {
  if (m.is_owner) return <Badge colorScheme="purple" borderRadius="full" px="9px" fontSize="10.5px">Organizator</Badge>;
  if (m.moderator) return <Badge colorScheme="blue" borderRadius="full" px="9px" fontSize="10.5px">Moderator</Badge>;
  if (m.blocked) return <Badge colorScheme="red" borderRadius="full" px="9px" fontSize="10.5px">Zablokowany</Badge>;
  return null;
};

const PeoplePanel: React.FC<{ groupId: string; canManage: boolean; initialStatus?: string }> = ({ groupId, canManage, initialStatus }) => {
  const toast = useToast();
  // Deep-link może wskazać worek startowy (np. „Oczekujący" z powiadomienia o prośbie do grupy).
  const startStatus = STATUS_TABS.some(t => t.key === initialStatus) ? (initialStatus as GroupMemberStatus) : "account";
  const [status, setStatus] = useState<GroupMemberStatus>(startStatus);
  const [q, setQ] = useState("");
  const [members, setMembers] = useState<GroupMemberDetail[]>([]);
  const [meta, setMeta] = useState<GroupMembersMeta | null>(null);
  const [loading, setLoading] = useState(true);

  const load = (st: GroupMemberStatus, query: string) => {
    setLoading(true);
    getGroupMembers(groupId, { status: st, q: query || undefined })
      .then(r => {
        setMembers(r.data.members ?? []);
        setMeta(r.data.meta);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => load(status, q), q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [status, q, groupId]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast({ title: label, status: "success", duration: 1800, isClosable: true });
      load(status, q);
    } catch (e: any) {
      toast({ title: e?.response?.data?.error || "Nie udało się wykonać akcji", status: "error", isClosable: true });
    }
  };

  const canAppoint = meta?.can_appoint ?? false;

  return (
    <Box bg="white" borderRadius="18px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p={{ base: "16px", md: "18px 22px" }}>
      <Flex justify="space-between" align="center" mb="14px" gap="12px" wrap="wrap">
        <Text fontSize="17px" fontWeight="800" color="primary">
          Osoby {meta ? `· ${canManage ? meta.active_count : meta.account_count ?? 0}` : ""}
        </Text>
        <InputGroup maxW="260px" size="sm">
          <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Szukaj osoby…" borderRadius="11px" />
        </InputGroup>
      </Flex>

      {canManage && (
        <HStack spacing="6px" mb="14px" bg="#F4F0FB" p="4px" borderRadius="12px" w="fit-content" overflowX="auto" maxW="100%">
          {STATUS_TABS.map(t => {
            const count =
              t.key === "account" ? meta?.account_count
              : t.key === "email" ? meta?.email_only_count
              : t.key === "pending" ? meta?.pending_count
              : meta?.blocked_count;
            return (
              <Button
                key={t.key}
                size="xs"
                onClick={() => setStatus(t.key)}
                bg={status === t.key ? "white" : "transparent"}
                color={status === t.key ? "primary" : "gray.500"}
                boxShadow={status === t.key ? "0 2px 8px rgba(67,0,152,0.08)" : "none"}
                fontWeight="700"
                borderRadius="9px"
                px="14px"
              >
                {t.label}{typeof count === "number" ? ` (${count})` : ""}
              </Button>
            );
          })}
        </HStack>
      )}

      {canManage && BUCKET_HINT[status] && (
        <Flex bg="rgba(245,158,11,0.08)" border="1px solid rgba(245,158,11,0.25)" borderRadius="12px" p="10px 14px" mb="12px" gap="9px" align="center">
          <Icon as={FiMail} color="#b45309" boxSize="15px" flexShrink={0} />
          <Text fontSize="12.5px" color="#92400e">{BUCKET_HINT[status]}</Text>
        </Flex>
      )}

      {loading ? (
        <Flex justify="center" py="50px"><Spinner color="primary" thickness="3px" /></Flex>
      ) : members.length === 0 ? (
        <Flex direction="column" align="center" py="44px" color="gray.400">
          <Icon as={FiUsers} boxSize="22px" mb="8px" />
          <Text fontSize="14px">Brak osób w tej kategorii.</Text>
        </Flex>
      ) : (
        <VStack align="stretch" spacing="2px">
          {members.map(m => {
            const display = m.name || m.email;
            const avatar = (
              <Avatar size="sm" boxSize="40px" name={display} src={mediaUrl(m.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
            );
            return (
              <Flex key={m.id} align="center" justify="space-between" gap="10px" p="10px 8px" borderRadius="12px" _hover={{ bg: "#FAF8FE" }}>
                <HStack spacing="11px" minW={0}>
                  {m.profile_ref ? <Link to={`/profil/${m.profile_ref}`}>{avatar}</Link> : avatar}
                  <Box minW={0}>
                    <HStack spacing="8px">
                      {m.profile_ref ? (
                        <Text as={Link} to={`/profil/${m.profile_ref}`} fontWeight="700" color="primary" fontSize="14.5px" noOfLines={1} _hover={{ textDecoration: "underline" }}>{display}</Text>
                      ) : (
                        <Text fontWeight="700" color="primary" fontSize="14.5px" noOfLines={1}>{display}</Text>
                      )}
                      <RoleBadge m={m} />
                    </HStack>
                    {m.name && <Text fontSize="12.5px" color="gray.400" noOfLines={1}>{m.email}</Text>}
                  </Box>
                </HStack>

                {canManage && !m.is_owner && !m.is_me && (
                  <Menu placement="bottom-end">
                    <MenuButton as={IconButton} aria-label="Akcje" icon={<FiMoreVertical />} size="sm" variant="ghost" color="gray.400" flexShrink={0} />
                    <MenuList minW="180px" borderRadius="13px" boxShadow="0 10px 30px rgba(67,0,152,0.14)">
                      {!m.confirmed && !m.blocked && (
                        <MenuItem icon={<FiUserCheck />} color="#1f9e18" onClick={() => act("Zatwierdzono", () => approveGroupMember(groupId, m.id))}>Zatwierdź</MenuItem>
                      )}
                      {canAppoint && !m.blocked && m.has_account && (
                        m.moderator ? (
                          <MenuItem icon={<FiUserCheck />} onClick={() => act("Odebrano moderatora", () => demoteGroupMember(groupId, m.id))}>Odbierz moderatora</MenuItem>
                        ) : (
                          <MenuItem icon={<FiShield />} onClick={() => act("Mianowano moderatorem", () => promoteGroupMember(groupId, m.id))}>Mianuj moderatorem</MenuItem>
                        )
                      )}
                      {m.blocked ? (
                        <MenuItem icon={<FiUserCheck />} onClick={() => act("Odblokowano", () => unblockGroupMember(groupId, m.id))}>Odblokuj</MenuItem>
                      ) : (
                        <MenuItem icon={<FiSlash />} color="#e51f44" onClick={() => act("Zablokowano", () => blockGroupMember(groupId, m.id))}>Zablokuj</MenuItem>
                      )}
                      <MenuItem icon={<FiUserMinus />} color="#e51f44" onClick={() => act("Usunięto z grupy", () => deleteGroupMember(groupId, m.id))}>Wyrzuć z grupy</MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </Flex>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default PeoplePanel;
